import type { Game, GameContext, GameMeta, InputState } from '../types';
import { AstroBubbleEngine, type Enemy } from './AstroBubbleEngine';
import { playShoot, playExplosion, playPowerup } from '../../audio/sfx';

type FloatingText = {
  text: string;
  x: number;
  y: number;
  vy: number;
  color: string;
  alpha: number;
  scale: number;
};

export class AstroBubbleGame implements Game {
  readonly meta: GameMeta = {
    id: 'astro-bubble',
    title: 'ASTRO BUBBLE GUARDIAN',
    blurb: 'Trapping invaders in energy bubbles and chain-pop combos.',
    tag: 'ARCADE',
    status: 'playable',
  };

  private ctx!: GameContext;
  private canvas!: HTMLCanvasElement;
  private canvasCtx!: CanvasRenderingContext2D;
  private engine!: AstroBubbleEngine;

  private width = 800;
  private height = 600;
  private floatingTexts: FloatingText[] = [];
  private starfield: { x: number; y: number; size: number; alpha: number; speed: number }[] = [];
  private disposed = false;

  private keyListener = (e: KeyboardEvent) => {
    if (e.key === 'r' || e.key === 'R') {
      if (this.engine.state === 'gameover' || this.engine.state === 'start') {
        this.engine.restart();
      }
    }
  };

  private clickListener = () => {
    if (this.engine.state === 'gameover' || this.engine.state === 'start') {
      this.engine.restart();
    }
  };

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-canvas';
    this.canvas.setAttribute('aria-label', 'Astro Bubble Guardian Canvas');
    ctx.mount.innerHTML = '';
    ctx.mount.appendChild(this.canvas);

    const cCtx = this.canvas.getContext('2d');
    if (!cCtx) throw new Error('Failed to get 2D canvas context');
    this.canvasCtx = cCtx;

    const rect = ctx.mount.getBoundingClientRect();
    this.width = rect.width || 800;
    this.height = rect.height || 600;

    this.canvas.width = this.width;
    this.canvas.height = this.height;

    this.engine = new AstroBubbleEngine(this.width, this.height);

    // Init background starfield
    this.starfield = [];
    for (let i = 0; i < 90; i++) {
      this.starfield.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.7 + 0.3,
        speed: Math.random() * 10 + 5,
      });
    }

    window.addEventListener('keydown', this.keyListener);
    this.canvas.addEventListener('click', this.clickListener);
  }

  update(dt: number, input: InputState): void {
    if (this.disposed || !this.engine) return;

    const prevBulletsCount = this.engine.bullets.length;
    const prevLives = this.engine.player.lives;

    // Check custom restart key input
    const inputWithRestart = {
      ...input,
      restartPressed: input.pause || false,
    };

    this.engine.update(dt, inputWithRestart);

    // Audio SFX Triggers
    if (this.engine.bullets.length > prevBulletsCount && this.engine.state === 'playing') {
      playShoot();
    }

    if (this.engine.player.lives < prevLives && this.engine.state === 'playing') {
      playExplosion();
    }

    // Process Pop Events for floating score text and SFX
    for (const pop of this.engine.popEvents) {
      if (pop.comboCount > 1) {
        playPowerup();
      } else {
        playExplosion();
      }

      const comboLabel = pop.comboCount > 1 ? ` COMBO x${pop.comboCount}!` : '';
      this.floatingTexts.push({
        text: `+${pop.scoreGained}${comboLabel}`,
        x: pop.x,
        y: pop.y,
        vy: -40,
        color: pop.comboCount > 1 ? '#ff00ff' : '#00ffff',
        alpha: 1.0,
        scale: Math.min(2.0, 1.0 + pop.comboCount * 0.2),
      });
    }

    // Update Floating Text
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      ft.y += ft.vy * dt;
      ft.alpha -= dt * 1.2;
      if (ft.alpha <= 0) {
        this.floatingTexts.splice(i, 1);
      }
    }

    // Render Frame
    this.render();
  }

  private render(): void {
    const ctx = this.canvasCtx;
    const bands = this.ctx ? this.ctx.getBands() : { energy: 0, kick: 0, pulse: 0, bass: 0, mids: 0, highs: 0 };
    const energy = bands.energy || 0;
    const kick = bands.kick || 0;

    // Clear background with deep space retro glow
    ctx.fillStyle = '#050814';
    ctx.fillRect(0, 0, this.width, this.height);

    // Render Audio-reactive Starfield
    ctx.fillStyle = '#ffffff';
    for (const star of this.starfield) {
      star.y = (star.y + star.speed * (1 + energy * 0.5) * 0.016) % this.height;
      ctx.globalAlpha = Math.min(1.0, star.alpha + kick * 0.3);
      ctx.fillRect(star.x, star.y, star.size, star.size);
    }
    ctx.globalAlpha = 1.0;

    // Render Background Grid Lines (Subtle vector style)
    ctx.strokeStyle = `rgba(0, 255, 255, ${0.05 + kick * 0.05})`;
    ctx.lineWidth = 1;
    const gridSize = 40;
    for (let x = 0; x < this.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, this.height);
      ctx.stroke();
    }
    for (let y = 0; y < this.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(this.width, y);
      ctx.stroke();
    }

    // Render Particles
    for (const pt of this.engine.particles) {
      ctx.fillStyle = pt.color;
      ctx.globalAlpha = Math.max(0, pt.life / pt.maxLife);
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, pt.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1.0;

    // Render Bullets
    ctx.strokeStyle = '#00ffff';
    ctx.fillStyle = '#ffffff';
    ctx.lineWidth = 2;
    for (const b of this.engine.bullets) {
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    // Render Enemies (Normal and Trapped Bubbles)
    for (const e of this.engine.enemies) {
      if (e.state === 'normal') {
        this.renderNormalEnemy(e);
      } else {
        this.renderTrappedBubble(e, energy, kick);
      }
    }

    // Render Player Ship
    if (this.engine.state === 'playing') {
      this.renderPlayer(energy, kick);
    }

    // Render Floating Text Overlays
    for (const ft of this.floatingTexts) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, ft.alpha);
      ctx.fillStyle = ft.color;
      ctx.shadowColor = ft.color;
      ctx.shadowBlur = 10;
      ctx.font = `bold ${Math.floor(18 * ft.scale)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(ft.text, ft.x, ft.y);
      ctx.restore();
    }

    // Render Game State Overlays (Start / Game Over)
    if (this.engine.state === 'start') {
      this.renderStartScreen();
    } else if (this.engine.state === 'gameover') {
      this.renderGameOverScreen();
    }
  }

  private renderNormalEnemy(e: Enemy): void {
    const ctx = this.canvasCtx;
    ctx.save();
    ctx.translate(e.x, e.y);

    if (e.type === 'scout') {
      // Neon Green Triangle
      ctx.strokeStyle = '#00ff66';
      ctx.shadowColor = '#00ff66';
      ctx.shadowBlur = 8;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(e.radius, 0);
      ctx.lineTo(-e.radius * 0.7, -e.radius * 0.8);
      ctx.lineTo(-e.radius * 0.7, e.radius * 0.8);
      ctx.closePath();
      ctx.stroke();
    } else if (e.type === 'chaser') {
      // Magenta Diamond
      ctx.strokeStyle = '#ff00ff';
      ctx.shadowColor = '#ff00ff';
      ctx.shadowBlur = 10;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(e.radius, 0);
      ctx.lineTo(0, e.radius);
      ctx.lineTo(-e.radius, 0);
      ctx.lineTo(0, -e.radius);
      ctx.closePath();
      ctx.stroke();
    } else if (e.type === 'heavy') {
      // Yellow Hexagon
      ctx.strokeStyle = '#ffff00';
      ctx.shadowColor = '#ffff00';
      ctx.shadowBlur = 12;
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3;
        const x = Math.cos(a) * e.radius;
        const y = Math.sin(a) * e.radius;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
    }

    ctx.restore();
  }

  private renderTrappedBubble(e: Enemy, energy: number, kick: number): void {
    const ctx = this.canvasCtx;
    const bubbleRadius = e.radius + 10 + kick * 2;

    ctx.save();
    ctx.translate(e.x, e.y);

    // Glowing Iridescent Bubble Body
    ctx.strokeStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 14 + energy * 6;
    ctx.lineWidth = 2.5;

    ctx.beginPath();
    ctx.arc(0, 0, bubbleRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Inner translucent fill
    ctx.fillStyle = 'rgba(0, 255, 255, 0.15)';
    ctx.beginPath();
    ctx.arc(0, 0, bubbleRadius, 0, Math.PI * 2);
    ctx.fill();

    // Highlight sheen arc
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, bubbleRadius * 0.75, -Math.PI * 0.8, -Math.PI * 0.3);
    ctx.stroke();

    // Inner trapped alien sprite (scaled down inside bubble)
    ctx.scale(0.6, 0.6);
    this.renderNormalEnemy({ ...e, x: 0, y: 0 });

    ctx.restore();
  }

  private renderPlayer(_energy: number, kick: number): void {
    const p = this.engine.player;
    const ctx = this.canvasCtx;

    // Blink when invulnerable
    if (p.invulnTimer > 0 && Math.floor(p.invulnTimer * 10) % 2 === 0) {
      return;
    }

    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);

    // Neon Ship Hull
    ctx.strokeStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 12 + kick * 5;
    ctx.lineWidth = 2.5;
    ctx.fillStyle = 'rgba(0, 255, 255, 0.2)';

    ctx.beginPath();
    ctx.moveTo(p.radius + 4, 0); // Nose tip
    ctx.lineTo(-p.radius, -p.radius * 0.8);
    ctx.lineTo(-p.radius * 0.4, 0); // Inset
    ctx.lineTo(-p.radius, p.radius * 0.8);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Invulnerability Shield Ring
    if (p.invulnTimer > 0) {
      ctx.strokeStyle = '#ff00ff';
      ctx.shadowColor = '#ff00ff';
      ctx.shadowBlur = 16;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, p.radius + 10, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  private renderStartScreen(): void {
    const ctx = this.canvasCtx;
    ctx.save();

    // Dark Overlay
    ctx.fillStyle = 'rgba(5, 8, 20, 0.85)';
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.textAlign = 'center';

    // Title
    ctx.fillStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 20;
    ctx.font = 'bold 36px sans-serif';
    ctx.fillText('ASTRO BUBBLE GUARDIAN', this.width / 2, this.height / 2 - 100);

    // Subtitle / Premise
    ctx.fillStyle = '#ff00ff';
    ctx.shadowColor = '#ff00ff';
    ctx.shadowBlur = 10;
    ctx.font = '18px sans-serif';
    ctx.fillText('RETRO ARCADE CROSSOVER', this.width / 2, this.height / 2 - 60);

    // Instructions
    ctx.fillStyle = '#ffffff';
    ctx.shadowBlur = 0;
    ctx.font = '16px sans-serif';
    ctx.fillText('• WASD / Arrow Keys : Rotate & Thrust', this.width / 2, this.height / 2);
    ctx.fillText('• SPACE / Fire : Shoot Energy Bullets', this.width / 2, this.height / 2 + 30);
    ctx.fillText('• Encase invaders in bubbles & pop them for CHAIN COMBO points!', this.width / 2, this.height / 2 + 60);

    // Start Prompt
    ctx.fillStyle = '#00ff66';
    ctx.shadowColor = '#00ff66';
    ctx.shadowBlur = 12;
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText('CLICK / PRESS FIRE / PRESS R TO START', this.width / 2, this.height / 2 + 130);

    ctx.restore();
  }

  private renderGameOverScreen(): void {
    const ctx = this.canvasCtx;
    ctx.save();

    ctx.fillStyle = 'rgba(5, 8, 20, 0.88)';
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.textAlign = 'center';

    ctx.fillStyle = '#ff0055';
    ctx.shadowColor = '#ff0055';
    ctx.shadowBlur = 20;
    ctx.font = 'bold 42px sans-serif';
    ctx.fillText('GAME OVER', this.width / 2, this.height / 2 - 80);

    ctx.fillStyle = '#ffffff';
    ctx.shadowBlur = 0;
    ctx.font = '22px sans-serif';
    ctx.fillText(`FINAL SCORE: ${this.engine.score}`, this.width / 2, this.height / 2 - 20);
    ctx.fillText(`HIGH SCORE: ${this.engine.highScore}`, this.width / 2, this.height / 2 + 20);

    ctx.fillStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 12;
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText('CLICK / PRESS FIRE / PRESS R TO RETRY', this.width / 2, this.height / 2 + 90);

    ctx.restore();
  }

  resize(w: number, h: number): void {
    this.width = Math.max(1, w);
    this.height = Math.max(1, h);
    if (this.canvas) {
      this.canvas.width = this.width;
      this.canvas.height = this.height;
    }
    if (this.engine) {
      this.engine.width = this.width;
      this.engine.height = this.height;
    }
  }

  getHud() {
    return {
      score: this.engine ? this.engine.score : 0,
      lives: this.engine ? Math.max(0, this.engine.player.lives) : 3,
      wave: this.engine ? this.engine.wave : 1,
      message:
        this.engine && this.engine.state === 'gameover'
          ? 'GAME OVER — PRESS R / FIRE'
          : this.engine && this.engine.state === 'start'
          ? 'PRESS FIRE / R TO START'
          : undefined,
    };
  }

  dispose(): void {
    this.disposed = true;
    window.removeEventListener('keydown', this.keyListener);
    if (this.canvas) {
      this.canvas.removeEventListener('click', this.clickListener);
      if (this.canvas.parentElement) {
        this.canvas.parentElement.removeChild(this.canvas);
      }
    }
  }
}
